import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Form,
  FormRow,
  FormSection,
  Input,
  Select,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
} from '../ui';
import type {
  BolForm__BillOfLadingFragment,
  OrderForm_OrderAttributeFragment,
  PortCallsDocuments__OrganizationFragment,
} from '../../graphql';
import {
  useCreateBolDatumMutation,
  useCreateBolMutation,
  useUpdateBolDatumMutation,
  VolumeUnits,
  WeightUnits,
} from '../../graphql';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as zod from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ReactSelect from 'react-select';
import { stringToNumber } from '../../lib/zod-util';
import type { Id } from '../../lib/util';
import { assertDefined, isDefined, mapKeyBy } from '../../lib/util';
import intersectionWith from 'lodash/intersectionWith';
import differenceWith from 'lodash/differenceWith';
import {
  massCapacityUnits,
  volumeCapacityUnits,
} from '../../constants/capacityUnits';
import omit from 'lodash/omit';
import {
  getOrderDisplayName,
  useUpdateOrderAttributesMutation,
} from '../order/order';
import { valueAndLabel } from '../order/OrderForm';
import { gql, useApolloClient } from '@apollo/client';
import type { ScenarioOrder } from '../../lib/planningData';

const sharedKeys = <A extends string, B extends string>(
  a: { [K in A]?: unknown },
  b: { [K in B]?: unknown },
): (A & B)[] => (Object.keys(a) as A[]).filter((k): k is A & B => k in b);

export interface BOLFormProps {
  bol?: BolForm__BillOfLadingFragment;
  onSave?: () => void;
  orders: ScenarioOrder[];
  organizations: Map<number, PortCallsDocuments__OrganizationFragment>;
  orderAttributes: OrderForm_OrderAttributeFragment[];
  portCallId: number;
  userId: number;
  readOnly?: boolean;
}

const BOLDatumSchema = zod.object({
  id: zod.number().nullable(),
  orderId: zod.number().nullable(),
  description: zod.string().nullable(),
  volume: zod.number().nullable(),
  volumeUnit: zod.nativeEnum(VolumeUnits).nullable(),
  weight: zod.number().nullable(),
  weightUnit: zod.nativeEnum(WeightUnits).nullable(),
  volumeOnDeck: zod.number().nullable(),
  weightOnDeck: zod.number().nullable(),
  orderAttributes: zod.array(valueAndLabel),
});

type BOLDatumType = zod.infer<typeof BOLDatumSchema>;

const BOLSchema = zod.object({
  id: zod.number().nullable(),
  portCallId: zod.number(),
  bolData: zod.array(BOLDatumSchema).optional(),
});

type BOLFormFields = zod.infer<typeof BOLSchema>;

const massUnitOptions = Object.keys(massCapacityUnits).map(
  (key: WeightUnits) => ({
    name: massCapacityUnits[key].symbol,
    id: key,
  }),
);

const volumeUnitOptions = Object.keys(volumeCapacityUnits).map(
  (key: VolumeUnits) => ({
    name: volumeCapacityUnits[key].symbol,
    id: key,
  }),
);

const BOLForm = ({
  bol,
  onSave,
  orders,
  portCallId,
  organizations,
  orderAttributes,
  userId,
  readOnly,
}: BOLFormProps): JSX.Element => {
  const [
    createBol,
    { data: createBolData, error: createBolError, loading: createBolLoading },
  ] = useCreateBolMutation({
    update: (cache, { data }) => {
      const bol = data?.createBillOfLading?.billOfLading;
      if (!bol) return;

      const newBolRef = cache.writeFragment({
        data: bol,
        fragment: gql`
          fragment NewBOL on BillOfLading {
            nodeId
            id
            portCallId
          }
        `,
      });

      cache.modify({
        id: cache.identify({
          __typename: 'PortCall',
          id: bol.portCallId,
        }),
        fields: {
          billOfLadingsByPortCallId: (existingRefs) => ({
            nodes: [...existingRefs.nodes, newBolRef],
          }),
        },
        broadcast: false,
      });
    },
  });
  const [
    createBolDatum,
    {
      data: createBolDatumData,
      error: createBolDatumError,
      loading: createBolDatumLoading,
    },
  ] = useCreateBolDatumMutation({
    update: (cache, { data }) => {
      const bolDatum = data?.createBillOfLadingDatum?.billOfLadingDatum;
      if (!bolDatum) return;

      const newBolDatumRef = cache.writeFragment({
        data: bolDatum,
        fragment: gql`
          fragment NewBolDatum on BillOfLadingDatum {
            id
            billOfLadingId
            orderId
            description
            volume
            volumeUnit
            weight
            weightUnit
            volumeOnDeck
            weightOnDeck
          }
        `,
      });

      cache.modify({
        id: cache.identify({
          __typename: 'BillOfLading',
          id: bolDatum.billOfLadingId,
        }),
        fields: {
          billOfLadingDataByBillOfLadingId: (existingRefs) => ({
            nodes: [...existingRefs.nodes, newBolDatumRef],
          }),
        },
        broadcast: false,
      });
    },
  });
  const [
    updateBolDatum,
    {
      data: updateBolDatumData,
      error: updateBolDatumError,
      loading: updateBolDatumLoading,
    },
  ] = useUpdateBolDatumMutation();
  const [updateOrderAttributes, { loading: updateOrderAttributesLoading }] =
    useUpdateOrderAttributesMutation();

  const [attributeSaveSuccess, setAttributeSaveSuccess] = useState<
    boolean | undefined
  >(undefined);
  const saveLoading =
    createBolLoading ||
    createBolDatumLoading ||
    updateBolDatumLoading ||
    updateOrderAttributesLoading;
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<boolean>(false);

  const orderById = useMemo(() => mapKeyBy(orders, (o) => o.id), [orders]);

  const orderAttributeOptions = useMemo(
    () =>
      orderAttributes.map((sr) => ({
        value: sr?.id ?? -1,
        label: sr?.name ?? '',
      })) ?? [],
    [orderAttributes],
  );

  useEffect(
    () =>
      setSaveSuccess(
        !!createBolData ||
          !!createBolDatumData ||
          !!updateBolDatumData ||
          !!attributeSaveSuccess,
      ),
    [
      createBolData,
      createBolDatumData,
      updateBolDatumData,
      attributeSaveSuccess,
    ],
  );
  useEffect(
    () =>
      setSaveError(
        !!createBolError ||
          !!createBolDatumError ||
          !!updateBolDatumError ||
          attributeSaveSuccess === false,
      ),
    [
      createBolError,
      createBolDatumError,
      updateBolDatumError,
      attributeSaveSuccess,
    ],
  );
  useEffect(() => {
    setSaveSuccess(false);
    setSaveError(false);
  }, [portCallId]);

  const defaultValues = useMemo<BOLFormFields>(() => {
    const bolData =
      bol?.billOfLadingDataByBillOfLadingId.nodes.filter(isDefined).map(
        (bolDatum): BOLDatumType => ({
          id: bolDatum.id,
          orderId: bolDatum.orderId ?? null,
          description: bolDatum.description ?? null,
          volume: bolDatum.volume ? +bolDatum.volume : null,
          volumeUnit: bolDatum.volumeUnit ?? VolumeUnits.M_3,
          weight: bolDatum.weight ? +bolDatum.weight : null,
          weightUnit: bolDatum.weightUnit ?? WeightUnits.TONNE,
          volumeOnDeck: bolDatum.volumeOnDeck ? +bolDatum.volumeOnDeck : null,
          weightOnDeck: bolDatum.weightOnDeck ? +bolDatum.weightOnDeck : null,
          orderAttributes:
            orderById
              .get(bolDatum.orderId ?? NaN)
              ?.orderAttributeAssociationsByOrderId?.nodes?.map((n) =>
                orderAttributeOptions.find(
                  (sr) => sr.value === n?.orderAttributeId,
                ),
              )
              .filter(isDefined) ?? [],
        }),
      ) ?? [];

    // Add empty entry for all orders that do not have saved data available
    const missingBolData = orders
      .filter(
        (order) =>
          bolData.findIndex((bolDatum) => bolDatum.orderId === order.id) < 0,
      )
      .map(
        (order): BOLDatumType => ({
          id: null,
          orderId: order.id,
          description: null,
          volume: null,
          volumeUnit: VolumeUnits.M_3,
          weight: null,
          weightUnit: WeightUnits.TONNE,
          volumeOnDeck: null,
          weightOnDeck: null,
          orderAttributes:
            order.orderAttributeAssociationsByOrderId?.nodes
              ?.map((n) =>
                orderAttributeOptions.find(
                  (sr) => sr.value === n?.orderAttributeId,
                ),
              )
              .filter(isDefined) ?? [],
        }),
      );

    return {
      id: bol?.id ?? null,
      portCallId,
      // always display bol items in certain order
      bolData: [...bolData, ...missingBolData].sort((a, b) => {
        if (a.orderId !== null && b.orderId !== null) {
          return a.orderId - b.orderId;
        }
        return -1;
      }),
    };
  }, [bol, orders, portCallId, orderAttributeOptions, orderById]);

  const { control, handleSubmit, register, reset } = useForm<BOLFormFields>({
    defaultValues,
    resolver: zodResolver(BOLSchema),
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { fields } = useFieldArray({
    control,
    name: 'bolData',
    keyName: 'key',
  });

  const client = useApolloClient();

  const onFormSubmit = async (data: BOLFormFields) => {
    try {
      const { id, bolData = [] } = BOLSchema.parse(data);

      let bolId: number;

      if (id === null) {
        const { data } = await createBol({
          variables: {
            input: {
              billOfLading: {
                portCallId,
                userId,
                date: new Date().toISOString(),
              },
            },
          },
        });

        const createdBolId = data?.createBillOfLading?.billOfLading?.id;
        assertDefined(
          createdBolId,
          'New BoL row should have an ID. The save must have failed.',
        );
        bolId = createdBolId;
      } else {
        bolId = id;
      }

      const oldBolData =
        bol?.billOfLadingDataByBillOfLadingId.nodes.filter(isDefined) ?? [];

      const bolDataToUpdate = intersectionWith(
        bolData,
        oldBolData,
        (a, b) =>
          a.id === b.id &&
          sharedKeys(a, omit(b, 'nodeId', '__typename')).some(
            (key) => a[key] != b[key],
          ),
      ).filter(
        (bolDatum): bolDatum is Id<typeof bolDatum> => bolDatum.id !== null,
      );

      const bolDataToCreate = differenceWith(
        bolData,
        oldBolData,
        (a, b) => a.id === b.id,
      );

      // Save bol data rows for each order

      await Promise.all(
        bolDataToUpdate.map((bolDatum) =>
          updateBolDatum({
            variables: {
              input: {
                id: bolDatum.id,
                billOfLadingDatumPatch: omit(bolDatum, 'id', 'orderAttributes'),
              },
            },
          }),
        ),
      );

      await Promise.all(
        bolDataToCreate.map((bolDatum) =>
          createBolDatum({
            variables: {
              input: {
                billOfLadingDatum: {
                  billOfLadingId: bolId,
                  ...omit(bolDatum, 'id', 'orderAttributes'),
                },
              },
            },
          }),
        ),
      );

      // Save all order attribute associations for all bol rows
      setAttributeSaveSuccess(undefined);

      const attributeSaveResponses = await Promise.all(
        bolData
          .filter(
            (bd): bd is BOLDatumType & { orderId: number } =>
              typeof bd.orderId === 'number',
          )
          .map((bolDatum) => {
            const newAttributes = (bolDatum?.orderAttributes || []).map(
              (x) => ({
                orderAttributeId: x.value,
              }),
            );
            const oldAttributes =
              orderById
                .get(bolDatum.orderId)
                ?.orderAttributeAssociationsByOrderId?.nodes.filter(
                  isDefined,
                ) ?? [];

            return updateOrderAttributes(
              bolDatum.orderId,
              newAttributes,
              oldAttributes,
            );
          }),
      );

      if (
        attributeSaveResponses.every(
          (res) => res.errors && res.errors.length > 0,
        )
      ) {
        setAttributeSaveSuccess(false);
      } else {
        setAttributeSaveSuccess(true);
      }

      await client.refetchQueries({
        include: ['VoyagesTable_BillOfLadings'],
      });

      onSave?.();
    } catch (error) {
      console.error('Error saving SOF', error);
    }
  };

  return (
    <Form border="none" onSubmit={handleSubmit(onFormSubmit)}>
      <input
        type="hidden"
        {...register('id', { setValueAs: stringToNumber })}
      />
      <input
        type="hidden"
        {...register('portCallId', { setValueAs: stringToNumber })}
      />
      <FormSection title="Bill of lading">
        <Box
          width="100%"
          borderWidth={1}
          borderStyle="solid"
          borderColor="neutral.gray_225"
          mb={3}
          overflow="scroll"
        >
          <Table style={{ tableLayout: 'auto' }}>
            <thead>
              <TableHeaders>
                <TableHeader minWidth={120}>Order ID</TableHeader>
                <TableHeader minWidth={150}>Receiving</TableHeader>
                <TableHeader minWidth={100}>Commodity</TableHeader>
                <TableHeader minWidth={200}>Attributes</TableHeader>
                <TableHeader minWidth={200}>Description</TableHeader>
                <TableHeader minWidth={125}>Planned</TableHeader>
                <TableHeader minWidth={250}>Gross weight</TableHeader>
                <TableHeader minWidth={250}>Volume</TableHeader>
                <TableHeader minWidth={200}>Weight on deck</TableHeader>
                <TableHeader minWidth={200}>Volume on deck</TableHeader>
                <TableHeader />
              </TableHeaders>
            </thead>
            <tbody>
              {fields.map((field, index) => {
                const order = orders.find(
                  (order) => order.id === field.orderId,
                );
                return (
                  order && (
                    <TableRow key={field.orderId}>
                      <TableCell>
                        <input
                          type="hidden"
                          {...register(`bolData.${index}.id`, {
                            setValueAs: stringToNumber,
                          })}
                          defaultValue={Number(field.id)}
                        />
                        <input
                          type="hidden"
                          {...register(`bolData.${index}.orderId`, {
                            setValueAs: stringToNumber,
                          })}
                          defaultValue={Number(field.orderId)}
                        />
                        {order && getOrderDisplayName(order)}
                      </TableCell>
                      <TableCell>
                        {organizations.get(
                          order.orderDetailsByOrderId.nodes[0]
                            ?.receiverOrganisationId ?? NaN,
                        )?.name ?? 'No name'}
                      </TableCell>
                      <TableCell>
                        {order.assortmentByAssortmentId?.name}
                      </TableCell>
                      <TableCell>
                        <Controller
                          control={control}
                          name={`bolData.${index}.orderAttributes`}
                          defaultValue={field.orderAttributes ?? []}
                          render={({ field: { value, onChange } }) => (
                            <ReactSelect
                              options={orderAttributeOptions}
                              placeholder="Attributes"
                              isMulti
                              value={
                                value ||
                                field.orderAttributes.map(
                                  (sel: (typeof orderAttributeOptions)[0]) =>
                                    orderAttributeOptions.find(
                                      (sr) => sr.value === sel.value,
                                    ),
                                )
                              }
                              onChange={(value) => onChange(value || [])}
                              menuPosition="fixed"
                              menuPlacement="top"
                              isDisabled={readOnly}
                              styles={{
                                container: (styles) => ({
                                  ...styles,
                                  width: '100%',
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 100,
                                }),
                                control: (provided) => ({
                                  ...provided,
                                  flexWrap: 'nowrap',
                                }),
                              }}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          {...register(`bolData.${index}.description`)}
                          defaultValue={field.description ?? undefined}
                          disabled={readOnly}
                        />
                      </TableCell>
                      <TableCell>
                        {`${order.cargoAmount} ${
                          order.cargoUnitByCargoUnitId?.name ?? ''
                        }`}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="row">
                          <Input
                            type="number"
                            {...register(`bolData.${index}.weight`, {
                              setValueAs: stringToNumber,
                            })}
                            defaultValue={field.weight ?? undefined}
                            mr={1}
                            flex={1}
                            disabled={readOnly}
                          />
                          <Select
                            {...register(`bolData.${index}.weightUnit`)}
                            defaultValue={field.weightUnit ?? undefined}
                            options={massUnitOptions}
                            width={48}
                            disabled={readOnly}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="row">
                          <Input
                            type="number"
                            {...register(`bolData.${index}.volume`, {
                              setValueAs: stringToNumber,
                            })}
                            defaultValue={field.volume ?? undefined}
                            mr={1}
                            flex={1}
                            disabled={readOnly}
                          />
                          <Select
                            {...register(`bolData.${index}.volumeUnit`)}
                            defaultValue={field.volumeUnit ?? undefined}
                            options={volumeUnitOptions}
                            width={48}
                            disabled={readOnly}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          {...register(`bolData.${index}.weightOnDeck`, {
                            setValueAs: stringToNumber,
                          })}
                          defaultValue={field.weightOnDeck ?? undefined}
                          disabled={readOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          {...register(`bolData.${index}.volumeOnDeck`, {
                            setValueAs: stringToNumber,
                          })}
                          defaultValue={field.volumeOnDeck ?? undefined}
                          disabled={readOnly}
                        />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )
                );
              })}
            </tbody>
          </Table>
        </Box>
        {!readOnly && (
          <FormRow alignItems="center">
            <Button
              disabled={saveLoading}
              variant="secondary"
              alignSelf="flex-start"
              onPress={handleSubmit(onFormSubmit)}
              mr={3}
            >
              Save Bill of Lading
            </Button>
            {saveSuccess && (
              <Text fontWeight="bold" color="#5fad09">
                ✓ Saved successfully
              </Text>
            )}
            {saveError && (
              <Text fontWeight="bold" color="#FC4A15">
                ✗ Error saving
              </Text>
            )}
          </FormRow>
        )}
      </FormSection>
    </Form>
  );
};

export default BOLForm;
